import { API_CONFIG } from "../config/constants";
import {
  ApiError,
  CreateShareRequest,
  CreateShareResponse,
  PublicVideoResponse,
  SetPublicResponse,
  SharedClipResponse,
} from "../types/api";
import { IS_MOCK, MockApiService } from "./mock";

export interface ApiClient {
  get<T>(endpoint: string, authToken?: string): Promise<T>;
  post<T>(endpoint: string, data: unknown, authToken?: string): Promise<T>;
  getVideoUrl(videoKey: string, authToken: string): Promise<{ url: string }>;
  createShare(
    request: CreateShareRequest,
    authToken: string,
  ): Promise<CreateShareResponse>;
  getSharedClip(token: string): Promise<SharedClipResponse>;
  setVideoPublic(
    videoKey: string,
    isPublic: boolean,
    authToken: string,
  ): Promise<SetPublicResponse>;
  getPublicVideo(slug: string): Promise<PublicVideoResponse>;
}

export class ApiService implements ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
    if (!this.baseUrl) {
      throw new Error("API_CONFIG.baseUrl is not configured");
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error: ApiError = {
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      };
      throw error;
    }

    return await response.json();
  }

  private request<T>(
    method: string,
    endpoint: string,
    data?: unknown,
    authToken?: string,
  ): Promise<T> {
    const headers: Record<string, string> = {};

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    return this.makeRequest<T>(endpoint, {
      method,
      headers,
      body: data === undefined ? undefined : JSON.stringify(data),
    });
  }

  get<T>(endpoint: string, authToken?: string): Promise<T> {
    return this.request<T>("GET", endpoint, undefined, authToken);
  }

  post<T>(endpoint: string, data: unknown, authToken?: string): Promise<T> {
    return this.request<T>("POST", endpoint, data, authToken);
  }

  put<T>(endpoint: string, data: unknown, authToken?: string): Promise<T> {
    return this.request<T>("PUT", endpoint, data, authToken);
  }

  async getVideoUrl(
    videoKey: string,
    authToken: string,
  ): Promise<{ url: string }> {
    return this.get<{ url: string }>(
      `${API_CONFIG.endpoints.videoUrl}/${encodeURIComponent(videoKey)}`,
      authToken,
    );
  }

  async createShare(
    request: CreateShareRequest,
    authToken: string,
  ): Promise<CreateShareResponse> {
    return this.post<CreateShareResponse>(
      API_CONFIG.endpoints.share,
      request,
      authToken,
    );
  }

  // Public endpoint -- intentionally unauthenticated.
  async getSharedClip(token: string): Promise<SharedClipResponse> {
    return this.get<SharedClipResponse>(
      `${API_CONFIG.endpoints.sharedClip}?token=${encodeURIComponent(token)}`,
    );
  }

  async setVideoPublic(
    videoKey: string,
    isPublic: boolean,
    authToken: string,
  ): Promise<SetPublicResponse> {
    return this.put<SetPublicResponse>(
      `${API_CONFIG.endpoints.videoPublic}/${encodeURIComponent(videoKey)}/public`,
      { public: isPublic },
      authToken,
    );
  }

  // Public endpoint -- intentionally unauthenticated.
  async getPublicVideo(slug: string): Promise<PublicVideoResponse> {
    return this.get<PublicVideoResponse>(
      `${API_CONFIG.endpoints.publicVideo}?slug=${encodeURIComponent(slug)}`,
    );
  }
}

export const apiService: ApiClient = IS_MOCK
  ? new MockApiService()
  : new ApiService();
